import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, Circle, Award, Menu } from "lucide-react";
import portalApi, { formatPortalError } from "./portalApi";
import api from "../../lib/api";
import { PortalHeader, useLearner, buildSections, computeProgress } from "./PortalDashboard";
import PreviewBanner from "./PreviewBanner";
import { previewStore, buildPreviewCourseBundle } from "./previewStore";

export default function PortalLearn() {
  const learner = useLearner();
  const { sectionId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [navOpen, setNavOpen] = useState(false);
  const [previewCourse, setPreviewCourse] = useState(null);
  const inPreview = previewStore.isActive();

  const load = async () => {
    if (inPreview) {
      let c = previewCourse;
      if (!c) {
        try {
          const r = await api.get("/admin/portal/preview/course");
          c = r.data.course;
          setPreviewCourse(c);
        } catch {
          toast.error("Preview requires an active admin session.");
          previewStore.exit();
          nav("/care-portal/admin/login");
          return;
        }
      }
      setData(buildPreviewCourseBundle(c));
      return;
    }
    try { const r = await portalApi.get("/portal/courses/CTA-001"); setData(r.data); }
    catch (err) { toast.error(formatPortalError(err)); }
  };
  useEffect(() => { if (learner) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [learner]);

  const sections = useMemo(() => data ? buildSections(data.course) : [], [data]);
  const currentIndex = sections.findIndex(s => s.id === sectionId);
  const current = sections[currentIndex];

  if (!learner || !data) return (
    <div className="min-h-screen bg-[#FDFCF6]">
      {inPreview && <PreviewBanner course={previewCourse} />}
      <PortalHeader learner={learner}/>
      <div className="p-12 text-center text-slate-500">Loading…</div>
    </div>
  );
  if (!current) { nav("/academy/portal/learn/welcome"); return null; }

  const completions = data.progress?.section_completions || {};
  const quizPassed = !!(data.best_quiz && data.best_quiz.passed);
  const progressInfo = computeProgress(sections, data.progress, quizPassed, data.evaluation);

  const isDone = (s) => {
    if (s.type === "welcome") return !!completions.welcome;
    if (s.type === "module") return !!completions[s.id];
    if (s.type === "case-study") return !!data.progress?.case_study_completed;
    if (s.type === "quiz") return quizPassed;
    if (s.type === "evaluation") return !!data.evaluation;
    if (s.type === "completion") return !!data.evaluation && !!data.certificate;
    return false;
  };

  const markComplete = async (secId) => {
    if (inPreview) { previewStore.markSectionComplete(secId); await load(); return; }
    try { await portalApi.post("/portal/progress/section-complete", { course_code: "CTA-001", section_id: secId }); await load(); }
    catch (err) { toast.error(formatPortalError(err)); }
  };

  const goNext = async () => {
    if (current.type === "welcome" || current.type === "module") await markComplete(current.id);
    const next = sections[currentIndex + 1];
    if (next) nav(`/academy/portal/learn/${next.id}`);
    setNavOpen(false);
  };
  const goPrev = () => {
    const prev = sections[currentIndex - 1];
    if (prev) nav(`/academy/portal/learn/${prev.id}`);
    setNavOpen(false);
  };

  return (
    <div className="bg-[#FDFCF6] min-h-screen" data-testid="portal-learn">
      {inPreview && <PreviewBanner course={previewCourse} onSyncPreview={load} />}
      <PortalHeader learner={learner}/>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 lg:hidden">
        <button onClick={()=>setNavOpen(v=>!v)} data-testid="learn-mobile-nav" className="inline-flex items-center gap-2 text-sm text-slate-600"><Menu className="w-4 h-4"/> Course Navigation</button>
      </div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-16 grid lg:grid-cols-[280px_1fr] gap-8">
        {/* Nav */}
        <aside data-testid="learn-nav" className={`${navOpen ? "block" : "hidden"} lg:block bg-white border border-slate-200 rounded-2xl p-4 lg:sticky lg:top-20 lg:self-start`}>
          <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Course Progress · {progressInfo.pct}%</p>
          <div className="h-1.5 bg-slate-100 rounded-full mb-4 overflow-hidden"><div className="h-full bg-[#4A6FA5]" style={{width:`${progressInfo.pct}%`}}/></div>
          <ol className="space-y-1">
            {sections.map((s, i) => {
              const done = isDone(s);
              const isCurrent = s.id === sectionId;
              return (
                <li key={s.id}>
                  <Link to={`/academy/portal/learn/${s.id}`} onClick={()=>setNavOpen(false)} data-testid={`nav-${s.id}`} className={`flex items-start gap-2 rounded-lg px-2.5 py-2 text-sm ${isCurrent ? "bg-[#F0F4F8] text-[#4A6FA5] font-medium" : "text-slate-700 hover:bg-slate-50"}`}>
                    {done ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0"/> : <Circle className="w-4 h-4 text-slate-300 mt-0.5 shrink-0"/>}
                    <span className="leading-snug">{s.label}</span>
                  </Link>
                </li>
              );
            })}
          </ol>
          <div className="mt-4 pt-4 border-t border-slate-100 text-center">
            <Link to="/academy/portal/dashboard" className="text-xs text-slate-500 hover:text-[#4A6FA5]">← Back to dashboard</Link>
          </div>
        </aside>

        {/* Main */}
        <main className="bg-white border border-slate-200 rounded-2xl p-6 md:p-10 min-w-0">
          {current.type === "welcome" && <WelcomeView course={data.course}/>}
          {current.type === "module" && <ModuleView module={current.module}/>}
          {current.type === "case-study" && <CaseStudyView course={data.course} progress={data.progress} onSaved={load} inPreview={inPreview}/>}
          {current.type === "quiz" && <QuizView course={data.course} latestQuiz={data.latest_quiz} bestQuiz={data.best_quiz} onSubmitted={load} inPreview={inPreview}/>}
          {current.type === "evaluation" && <EvaluationView course={data.course} quizPassed={quizPassed} evaluation={data.evaluation} onSubmitted={load} nav={nav} inPreview={inPreview}/>}
          {current.type === "completion" && <CompletionView data={data}/>}

          <div className="mt-10 pt-6 border-t border-slate-100 flex items-center justify-between">
            <button onClick={goPrev} disabled={currentIndex === 0} data-testid="learn-prev" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#4A6FA5] disabled:opacity-40"><ArrowLeft className="w-4 h-4"/> Previous</button>
            {current.type !== "quiz" && current.type !== "case-study" && current.type !== "evaluation" && current.type !== "completion" && (
              <button onClick={goNext} data-testid="learn-next" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B]">
                {currentIndex === 0 ? "Begin Course" : "Continue"} <ArrowRight className="w-4 h-4"/>
              </button>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

function WelcomeView({ course }) {
  return (
    <div data-testid="view-welcome">
      <p className="text-xs uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold">Welcome to CTA-001</p>
      <h1 className="mt-2 font-heading text-3xl sm:text-4xl text-slate-900 tracking-tight leading-tight">{course.title}</h1>
      <p className="mt-6 text-lg font-heading text-slate-800 italic">"{course.opening_principle}"</p>
      <p className="mt-6 text-slate-700 leading-relaxed">{course.chaplain_role_summary}</p>
      <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50/60 p-4 text-sm text-amber-900">
        {course.sensitivity_notice}
      </div>
      <div className="mt-8 rounded-xl bg-[#F0F4F8] p-5">
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#4A6FA5] font-semibold">Teaching model</p>
        <p className="mt-1.5 font-heading text-lg text-slate-900">{course.teaching_model.join("  →  ")}</p>
      </div>
    </div>
  );
}

function ModuleView({ module }) {
  return (
    <div data-testid={`view-${module.id}`}>
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Module {module.order}</p>
      <h1 className="mt-2 font-heading text-2xl sm:text-3xl text-slate-900 tracking-tight leading-tight">{module.title}</h1>
      <p className="mt-4 text-slate-700 leading-relaxed">{module.intro}</p>
      <div className="mt-8 space-y-6">
        {module.sections.map((s, i) => <SectionBlock key={i} section={s} idx={i}/>)}
      </div>
    </div>
  );
}

function SectionBlock({ section, idx }) {
  if (section.type === "text") return (
    <div>{section.heading && <h3 className="font-heading text-lg text-slate-900 mb-2">{section.heading}</h3>}
      <p className="text-slate-700 leading-relaxed">{section.body}</p></div>
  );
  if (section.type === "list") return (
    <div>{section.heading && <h3 className="font-heading text-lg text-slate-900 mb-2">{section.heading}</h3>}
      <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-slate-700">
        {section.items.map((it,i)=><li key={i} className="flex gap-2"><span className="text-[#4A6FA5]">•</span>{it}</li>)}
      </ul></div>
  );
  if (section.type === "highlight") return (
    <div className="rounded-xl border-l-4 border-[#4A6FA5] bg-[#F0F4F8] p-4">
      <p className="font-heading text-slate-900 leading-snug">{section.body}</p></div>
  );
  if (section.type === "reflection") return (
    <ReflectionField prompt={section.prompt} testid={`reflection-${idx}`}/>
  );
  if (section.type === "scenario") return <Scenario section={section} testid={`scenario-${idx}`}/>;
  if (section.type === "seven-roles") return (
    <div><h3 className="font-heading text-lg text-slate-900 mb-3">{section.heading}</h3>
      <div className="grid sm:grid-cols-2 gap-3">
        {section.items.map((r,i)=>(
          <div key={i} className="rounded-lg border border-slate-200 bg-white p-4">
            <p className="text-xs uppercase tracking-[0.16em] text-[#4A6FA5] font-semibold">{r.name}</p>
            <p className="mt-1 text-sm text-slate-700">{r.body}</p>
          </div>
        ))}
      </div></div>
  );
  if (section.type === "sorting-activity") return <SortingActivity section={section} testid={`sorting-${idx}`}/>;
  if (section.type === "helpful-unhelpful") return <HelpfulUnhelpful section={section} testid={`hu-${idx}`}/>;
  return null;
}

function ReflectionField({ prompt, testid }) {
  const [v,setV] = useState("");
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      <p className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold mb-2">Reflection</p>
      <p className="text-slate-800 mb-3">{prompt}</p>
      <textarea rows={3} value={v} onChange={e=>setV(e.target.value)} data-testid={testid} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]" placeholder="Your reflection stays private on this screen."/>
    </div>
  );
}

function Scenario({ section, testid }) {
  const [picked, setPicked] = useState(null);
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid={testid}>
      <p className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold mb-2">Scenario</p>
      <p className="text-slate-800 mb-3">{section.prompt}</p>
      <div className="space-y-2">
        {section.options.map(o => (
          <button key={o.key} onClick={()=>setPicked(o)} className={`w-full text-left rounded-lg border px-3 py-2.5 text-sm ${picked?.key===o.key ? (o.correct ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50") : "border-slate-200 bg-white hover:border-[#4A6FA5]"}`}>{o.label}</button>
        ))}
      </div>
      {picked && <p className={`mt-3 text-sm ${picked.correct ? "text-emerald-800" : "text-amber-900"}`}>{picked.correct ? "Yes. " : ""}{picked.feedback}</p>}
    </div>
  );
}

function SortingActivity({ section, testid }) {
  const [answers, setAnswers] = useState({});
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid={testid}>
      <p className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold mb-2">Activity</p>
      <p className="text-slate-800 mb-3">{section.prompt}</p>
      <div className="space-y-3">
        {section.items.map((it, i) => {
          const chosen = answers[i];
          const correct = chosen === it.category;
          return (
            <div key={i} className="rounded-lg border border-slate-200 bg-white p-3">
              <p className="text-sm text-slate-800 mb-2">{it.text}</p>
              <div className="flex flex-wrap gap-2">
                {section.categories.map(c => (
                  <button key={c} onClick={()=>setAnswers(s=>({...s,[i]:c}))} className={`text-xs px-2.5 py-1 rounded-full border ${chosen===c ? (correct ? "border-emerald-400 bg-emerald-50 text-emerald-800" : "border-amber-400 bg-amber-50 text-amber-900") : "border-slate-200 hover:border-[#4A6FA5]"}`}>{c}</button>
                ))}
              </div>
              {chosen && <p className={`mt-1.5 text-xs ${correct ? "text-emerald-700" : "text-amber-800"}`}>{correct ? "Correct." : `The best match is ${it.category}.`}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function HelpfulUnhelpful({ section, testid }) {
  const [picks, setPicks] = useState({});
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid={testid}>
      <p className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold mb-2">Helpful or Unhelpful?</p>
      <p className="text-slate-800 mb-3">{section.prompt}</p>
      <div className="space-y-3">
        {section.items.map((it, i) => {
          const pick = picks[i]; const correct = pick !== undefined && (pick === it.helpful);
          return (
            <div key={i} className="rounded-lg border border-slate-200 bg-white p-3">
              <p className="text-sm text-slate-800 mb-2">{it.text}</p>
              <div className="flex gap-2">
                <button onClick={()=>setPicks(s=>({...s,[i]:true}))} className={`text-xs px-3 py-1.5 rounded-full border ${pick===true ? (it.helpful ? "border-emerald-400 bg-emerald-50 text-emerald-800" : "border-amber-400 bg-amber-50 text-amber-900") : "border-slate-200 hover:border-[#4A6FA5]"}`}>Helpful</button>
                <button onClick={()=>setPicks(s=>({...s,[i]:false}))} className={`text-xs px-3 py-1.5 rounded-full border ${pick===false ? (!it.helpful ? "border-emerald-400 bg-emerald-50 text-emerald-800" : "border-amber-400 bg-amber-50 text-amber-900") : "border-slate-200 hover:border-[#4A6FA5]"}`}>Unhelpful</button>
              </div>
              {pick !== undefined && <p className={`mt-1.5 text-xs ${correct ? "text-emerald-700" : "text-amber-800"}`}>{it.feedback}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CaseStudyView({ course, progress, onSaved, inPreview }) {
  const cs = course.case_study;
  const [responses, setResponses] = useState(progress?.case_study_responses || Object.fromEntries(cs.prompts.map(p=>[p.id, ""])));
  const [revealed, setRevealed] = useState(!!progress?.case_study_completed);
  const [busy, setBusy] = useState(false);
  const allAnswered = cs.prompts.every(p => (responses[p.id] || "").trim().length >= 5);
  const submit = async () => {
    setBusy(true);
    try {
      if (inPreview) {
        previewStore.submitCaseStudy(responses);
        setRevealed(true); toast.success("Case study saved (preview only).");
        onSaved();
      } else {
        await portalApi.post("/portal/progress/case-study", { course_code: "CTA-001", responses });
        setRevealed(true); toast.success("Case study saved."); onSaved();
      }
    }
    catch (err) { toast.error(formatPortalError(err)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="view-case-study">
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Case Study</p>
      <h1 className="mt-2 font-heading text-2xl sm:text-3xl text-slate-900 tracking-tight">{cs.title}</h1>
      <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4 text-slate-800 leading-relaxed whitespace-pre-line">{cs.situation}</div>
      <p className="mt-6 text-sm text-slate-500">Respond to each prompt with a sentence or two. Your responses stay private.</p>
      <div className="mt-4 space-y-4">
        {cs.prompts.map((p, i) => (
          <div key={p.id}>
            <label className="block text-sm font-medium text-slate-800 mb-1.5">{i+1}. {p.text}</label>
            <textarea rows={3} value={responses[p.id] || ""} onChange={e=>setResponses(s=>({...s,[p.id]:e.target.value}))} data-testid={`case-study-${p.id}`} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]"/>
          </div>
        ))}
      </div>
      {!revealed ? (
        <button onClick={submit} disabled={!allAnswered || busy} data-testid="case-study-submit" className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B] disabled:opacity-50">
          {busy ? "Saving…" : "Submit Case Study"}
        </button>
      ) : (
        <div className="mt-6 rounded-xl border-l-4 border-[#4A6FA5] bg-[#F0F4F8] p-4" data-testid="case-study-teaching">
          <p className="text-xs uppercase tracking-[0.16em] text-[#4A6FA5] font-semibold mb-1">Central teaching point</p>
          <p className="font-heading text-slate-900">{cs.teaching_point}</p>
        </div>
      )}
    </div>
  );
}

function QuizView({ course, latestQuiz, bestQuiz, onSubmitted, inPreview }) {
  const quiz = course.quiz;
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(latestQuiz && !bestQuiz?.passed ? latestQuiz : (bestQuiz?.passed ? bestQuiz : null));
  const [busy, setBusy] = useState(false);
  const allAnswered = quiz.questions.every(q => answers[q.id]);
  const submit = async () => {
    setBusy(true);
    try {
      let r;
      if (inPreview) {
        const resp = await api.post("/admin/portal/preview/quiz-evaluate", { course_code: "CTA-001", answers });
        const attempt = previewStore.recordQuizAttempt(resp.data);
        r = { data: { ...resp.data, attempt_number: attempt.attempt_number } };
      } else {
        r = await portalApi.post("/portal/quiz/submit", { course_code: "CTA-001", answers });
      }
      setResult(r.data); onSubmitted();
      if (r.data.passed) toast.success(`Passed with ${r.data.score}/${r.data.total}!`);
      else toast.warning(`Scored ${r.data.score}/${r.data.total}. Review and retake when ready.`);
    } catch (err) { toast.error(formatPortalError(err)); } finally { setBusy(false); }
  };
  const retake = () => { setAnswers({}); setResult(null); };

  if (result && result.review) {
    const scoreBand = result.score >= 8 ? "bg-emerald-50 border-emerald-200 text-emerald-800" :
      result.score >= 6 ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-red-50 border-red-200 text-red-800";
    return (
      <div data-testid="view-quiz-result">
        <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Knowledge Check · Result</p>
        <h1 className="mt-2 font-heading text-2xl sm:text-3xl text-slate-900 tracking-tight">Your score: {result.score} / {result.total}</h1>
        <div className={`mt-4 rounded-xl border p-4 text-sm ${scoreBand}`}>
          {result.score >= 8 ? "You passed. You meet the recommended knowledge-check standard for this introductory training." :
            result.score >= 6 ? "Review the missed concepts below and reassess." :
            "Review the key course sections and reassess."}
        </div>
        <ol className="mt-6 space-y-3">
          {result.review.map((r, i) => (
            <li key={r.id} className={`rounded-lg border p-4 ${r.is_correct ? "border-emerald-200 bg-emerald-50/40" : "border-amber-200 bg-amber-50/50"}`}>
              <p className="text-sm text-slate-800"><span className="font-medium">{i+1}.</span> {r.prompt}</p>
              <p className="mt-2 text-xs text-slate-600">Your answer: <span className="font-medium">{r.selected?.toUpperCase() || "—"}</span> {r.is_correct ? "· Correct" : `· Correct: ${r.correct_key.toUpperCase()} — ${r.correct_text}`}</p>
              {!r.is_correct && r.lesson && <p className="mt-1 text-xs text-slate-500">Review: {r.lesson}</p>}
            </li>
          ))}
        </ol>
        {!result.passed && (
          <button onClick={retake} data-testid="quiz-retake" className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]">Retake Knowledge Check</button>
        )}
        {result.passed && (
          <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">Continue to the Course Evaluation to complete your certificate.</div>
        )}
        <p className="mt-6 text-[11px] text-slate-500 leading-relaxed">Passing the knowledge check does not independently certify clinical competence, legal knowledge, or readiness for unsupervised chaplain service.</p>
      </div>
    );
  }

  return (
    <div data-testid="view-quiz">
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Knowledge Check</p>
      <h1 className="mt-2 font-heading text-2xl sm:text-3xl text-slate-900 tracking-tight">10 questions · Pass at 8 of 10</h1>
      <p className="mt-3 text-slate-600 text-sm">Choose one answer per question. Unlimited retakes.</p>
      <div className="mt-6 space-y-6">
        {quiz.questions.map((q, i) => (
          <div key={q.id} data-testid={`quiz-q-${q.id}`}>
            <p className="text-sm font-medium text-slate-800 mb-2">{i+1}. {q.prompt}</p>
            <div className="space-y-1.5">
              {q.choices.map(c => (
                <label key={c.k} className={`flex items-start gap-3 rounded-lg border px-3 py-2 text-sm cursor-pointer ${answers[q.id]===c.k ? "border-[#4A6FA5] bg-[#F0F4F8]" : "border-slate-200 hover:border-slate-300"}`}>
                  <input type="radio" name={q.id} value={c.k} checked={answers[q.id]===c.k} onChange={()=>setAnswers(s=>({...s,[q.id]:c.k}))} data-testid={`quiz-${q.id}-${c.k}`} className="mt-0.5 h-4 w-4 text-[#4A6FA5]"/>
                  <span>{c.t}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
      <button onClick={submit} disabled={!allAnswered || busy} data-testid="quiz-submit" className="mt-8 inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B] disabled:opacity-50">
        {busy ? "Submitting…" : "Submit Knowledge Check"}
      </button>
    </div>
  );
}

function EvaluationView({ course, quizPassed, evaluation, onSubmitted, nav, inPreview }) {
  const [f, setF] = useState({ overall_rating: 0, clarity_rating: 0, understanding_rating: 0, most_useful: "", could_improve: "", next_course: "", testimonial_optin: false });
  const [busy, setBusy] = useState(false);

  if (!quizPassed) return (
    <div data-testid="view-evaluation-locked">
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Course Evaluation</p>
      <h1 className="mt-2 font-heading text-2xl text-slate-900">Complete the knowledge check first</h1>
      <p className="mt-3 text-slate-600">The evaluation opens after you pass the knowledge check with at least 8 of 10.</p>
    </div>
  );
  if (evaluation) return (
    <div data-testid="view-evaluation-done">
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Course Evaluation</p>
      <h1 className="mt-2 font-heading text-2xl text-slate-900">Thank you — your evaluation is submitted.</h1>
      <p className="mt-3 text-slate-600">Your certificate has been generated. Head to Completion to view or download it.</p>
    </div>
  );

  const submit = async e => {
    e.preventDefault();
    if (!f.overall_rating || !f.clarity_rating || !f.understanding_rating) { toast.error("Please rate all three items."); return; }
    setBusy(true);
    try {
      if (inPreview) {
        const res = previewStore.submitEvaluation(f);
        if (!res.ok) { toast.error(res.error); return; }
        toast.success("Evaluation submitted — sample certificate ready (preview only).");
        onSubmitted();
        if (res.certificate) nav("/academy/portal/learn/completion");
        return;
      }
      const r = await portalApi.post("/portal/evaluation", { course_code: "CTA-001", ...f });
      toast.success("Evaluation submitted — certificate ready.");
      onSubmitted();
      if (r.data.certificate) nav(`/academy/portal/learn/completion`);
    } catch (err) { toast.error(formatPortalError(err)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} data-testid="view-evaluation">
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Course Evaluation</p>
      <h1 className="mt-2 font-heading text-2xl sm:text-3xl text-slate-900 tracking-tight">One last step</h1>
      <p className="mt-3 text-slate-600 text-sm">Your feedback improves future CCCHAPS courses. Ratings are 1 (low) to 5 (high).</p>
      <div className="mt-6 space-y-5">
        <RatingRow label="Overall course rating" v={f.overall_rating} on={(n)=>setF(s=>({...s,overall_rating:n}))} testid="eval-overall"/>
        <RatingRow label="The material was clear" v={f.clarity_rating} on={(n)=>setF(s=>({...s,clarity_rating:n}))} testid="eval-clarity"/>
        <RatingRow label="The course increased my understanding of workplace grief support" v={f.understanding_rating} on={(n)=>setF(s=>({...s,understanding_rating:n}))} testid="eval-understanding"/>
        <div>
          <label className="block text-sm font-medium text-slate-800 mb-1.5">Most useful part of the course</label>
          <textarea rows={2} value={f.most_useful} onChange={e=>setF(s=>({...s,most_useful:e.target.value}))} data-testid="eval-most-useful" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]"/>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-800 mb-1.5">What could be improved?</label>
          <textarea rows={2} value={f.could_improve} onChange={e=>setF(s=>({...s,could_improve:e.target.value}))} data-testid="eval-could-improve" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]"/>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-800 mb-1.5">What CCCHAPS course would you like to take next? (optional)</label>
          <input value={f.next_course} onChange={e=>setF(s=>({...s,next_course:e.target.value}))} data-testid="eval-next-course" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]"/>
        </div>
        <label className="flex items-start gap-3 text-sm text-slate-700">
          <input type="checkbox" checked={f.testimonial_optin} onChange={e=>setF(s=>({...s,testimonial_optin:e.target.checked}))} data-testid="eval-testimonial" className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#4A6FA5]"/>
          <span>May CCCHAPS use my comments anonymously as a testimonial?</span>
        </label>
      </div>
      <button type="submit" disabled={busy} data-testid="eval-submit" className="mt-8 inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B] disabled:opacity-60">{busy ? "Submitting…" : "Submit & Generate Certificate"}</button>
    </form>
  );
}

function RatingRow({ label, v, on, testid }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-800 mb-1.5">{label}</label>
      <div className="flex gap-2" data-testid={testid}>
        {[1,2,3,4,5].map(n => (
          <button type="button" key={n} onClick={()=>on(n)} data-testid={`${testid}-${n}`} className={`w-10 h-10 rounded-full border text-sm font-medium ${v===n ? "border-[#4A6FA5] bg-[#4A6FA5] text-white" : "border-slate-300 text-slate-700 hover:border-[#4A6FA5]"}`}>{n}</button>
        ))}
      </div>
    </div>
  );
}

function CompletionView({ data }) {
  const cert = data.certificate;
  return (
    <div data-testid="view-completion">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 mb-4"><Award className="w-6 h-6"/></div>
      <p className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-semibold">CTA-001 · Completed</p>
      <h1 className="mt-2 font-heading text-3xl text-slate-900 tracking-tight">Congratulations, {data.enrollment?.status === "completed" ? "you've completed the course" : "you're almost there"}.</h1>
      <p className="mt-4 text-slate-700 leading-relaxed">Thank you for completing CTA-001 — Grief in the Workplace. Your certificate is available on your dashboard and below.</p>
      <div className="mt-6 rounded-xl bg-[#F0F4F8] p-5">
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#4A6FA5] font-semibold">Continue to remember</p>
        <p className="mt-1.5 font-heading text-lg text-slate-900">Recognize  →  Listen  →  Support  →  Assess  →  Refer</p>
      </div>
      {cert && (
        <div className="mt-6 rounded-xl border border-slate-200 p-5">
          <p className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">Certificate</p>
          <p className="mt-1 font-mono text-sm text-slate-900">{cert.cert_number}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to={`/academy/portal/certificate/${cert.id}`} data-testid="completion-view-cert" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B]"><Award className="w-4 h-4"/> View Certificate</Link>
          </div>
        </div>
      )}
      <p className="mt-6 text-[11px] text-slate-500 leading-relaxed">
        CTA-001 is introductory educational training. Completion does not confer professional licensure, clinical certification, accredited continuing-education credit, or authority to provide mental-health diagnosis or clinical treatment.
      </p>
    </div>
  );
}
